import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatMinutes } from "../../app/datetimeUtils";
import { useAppSelector } from "../../app/hooks";
import { isAdminRole, UserRole } from "../../app/userSlice";
import { Loading } from "../../components/Loading";
import {
	AdminStatCategories,
	AdminStatsViewModel,
	StatCategories,
	StatsViewModel,
} from "./homeApiSlice";

const initialStatsTabs = [
	{ name: "Last 7 days", current: true, codeName: "week" },
	{ name: "Last 30 days", current: false, codeName: "month" },
	{ name: "All-time", current: false, codeName: "allTime" },
];

const adminInitialStatsTabs = [
	{ name: "Last 30 days", current: true, codeName: "month" },
	{ name: "Last 90 days", current: false, codeName: "threeMonths" },
	{ name: "School Year", current: false, codeName: "schoolYear" },
];

interface Props {
	label: string;
	courseCreateDate?: string;
	loading: boolean;
	stats: StatsViewModel[] | AdminStatsViewModel[];
}

export default function Stats(props: Props) {
	const { config } = useAppSelector((state) => state.site);
	const { role } = useAppSelector((state) => state.user);

	const isAdmin = isAdminRole(role);

	const [statTabs, setStatTabs] = useState(isAdmin ? adminInitialStatsTabs : initialStatsTabs);
	const [statTab, setStatTab] = useState(
		isAdmin ? adminInitialStatsTabs[0].codeName : initialStatsTabs[0].codeName
	);

	const navigate = useNavigate();

	function handleStatTabChange(codeName: string): void {
		setStatTabs(
			statTabs.map((tab) =>
				tab.codeName === codeName ? { ...tab, current: true } : { ...tab, current: false }
			)
		);
		setStatTab(codeName);
	}

	useEffect(() => {
		if (props.courseCreateDate && !isAdmin) {
			const date = new Date(props.courseCreateDate);
			const month = date.toLocaleString("en-US", { month: "short" });
			const year = date.getFullYear();

			setStatTabs(
				statTabs.map((tab) =>
					tab.codeName === "allTime" ? { ...tab, name: `Since ${month} ${year}` } : tab
				)
			);
		}
	}, [props.courseCreateDate]);

	function handleStatNavigation(statCategory: StatCategories | AdminStatCategories) {
		if (isAdmin) return;
		switch (statCategory) {
			case StatCategories.Drafts:
				navigate("/writing");
				break;
			case StatCategories.Lessons:
				navigate("/lessons", { state: { statusFilter: "complete" } });
				break;
			case StatCategories.Reviews:
				role === UserRole.Proctor
					? navigate("/writing", { state: { sortField: "reviewsCompletedCount" } })
					: navigate("/reviews", { state: { assignedFilter: "complete" } });
				break;
			case StatCategories.Messages:
				role === UserRole.Proctor
					? navigate("/writing", { state: { sortField: "newMessagesCount" } })
					: navigate("/writing"); //in the future, maybe we should add a messages column to the writing tables
				break;
			case StatCategories.Score:
				role === UserRole.Proctor
					? navigate("/reports/course/progress")
					: navigate("/reports/student/progress");
				break;
		}
	}

	function getStatValue(
		stat: StatsViewModel | AdminStatsViewModel,
		tab: string
	): number | string {
		const shouldFormatAsMinutes = isAdmin && stat.sectionName === AdminStatCategories.Lessons;

		switch (tab) {
			case "week":
				return (stat as StatsViewModel).week;
			case "month":
				return shouldFormatAsMinutes ? formatMinutes(stat.month) : stat.month;
			case "threeMonths":
				return shouldFormatAsMinutes
					? formatMinutes((stat as AdminStatsViewModel).threeMonth)
					: (stat as AdminStatsViewModel).threeMonth;
			case "schoolYear":
				return shouldFormatAsMinutes
					? formatMinutes((stat as AdminStatsViewModel).schoolYear)
					: (stat as AdminStatsViewModel).schoolYear;
			default: // "allTime"
				return (stat as StatsViewModel).allTime;
		}
	}

	return (
		<div className="relative isolate overflow-hidden -mt-3">
			<header className="p-2">
				<div className="mx-auto px-6 flex flex-wrap items-center gap-6 sm:flex-nowrap">
					<h1 className="text-base font-semibold leading-7 text-gray-900">
						{props.label}
					</h1>
					<div className="order-last flex w-full gap-x-8 text-sm font-semibold leading-6 sm:order-none sm:w-auto sm:border-l sm:border-gray-200 sm:pl-6 sm:leading-7">
						{statTabs.map((item) => (
							<button
								id={item.name}
								key={item.name}
								role="tab"
								onClick={() => handleStatTabChange(item.codeName)}
								className={`${item.current ? (config?.isUtahComposeSite ? "text-[var(--subtab-border)] usage-tab-active" : "text-primary-600 usage-tab-active") : "text-gray-700"} hover:cursor-pointer`}>
								{item.name}
							</button>
						))}
					</div>
				</div>
			</header>
			<div className="border-y border-y-gray-900/10">
				<dl
					className={`mx-auto max-w-7xl px-2 grid ${!props.loading ? "grid-cols-2 lg:grid-cols-5" : ""} `}>
					<Loading loading={props.loading} className="!mt-0" />
					{props.stats.map((stat, statIdx) => (
						<div
							key={stat.sectionName}
							className={`${statIdx != 4 ? `${statIdx % 2 == 0 ? "border-r-2" : ""} ${isAdmin ? "hover:cursor-default" : "hover:cursor-pointer"} border-b lg:border-r-2 ` : "lg:border-r-none border-b-none col-span-2 lg:col-span-1"} flex flex-wrap items-baseline justify-between gap-y-1 px-6 border-gray-900/5 py-4`}
							onClick={() => handleStatNavigation(stat.sectionName)}>
							<dt className="text-sm font-medium leading-6 text-gray-600">
								{stat.sectionName}
							</dt>
							<dd className="w-full flex-none text-3xl font-medium leading-10 tracking-tight text-gray-900">
								<p>{getStatValue(stat, statTab)}</p>
							</dd>
						</div>
					))}
				</dl>
			</div>
			<div
				aria-hidden="true"
				className="absolute left-0 top-full -z-10 mt-96 origin-top-left translate-y-40 -rotate-90 transform-gpu opacity-20 blur-3xl sm:left-1/2 sm:-ml-96 sm:-mt-10 sm:translate-y-0 sm:rotate-0 sm:transform-gpu sm:opacity-50">
				<div
					style={{
						clipPath:
							"polygon(100% 38.5%, 82.6% 100%, 60.2% 37.7%, 52.4% 32.1%, 47.5% 41.8%, 45.2% 65.6%, 27.5% 23.4%, 0.1% 35.3%, 17.9% 0%, 27.7% 23.4%, 76.2% 2.5%, 74.2% 56%, 100% 38.5%)",
					}}
					className="aspect-[1154/678] w-[72.125rem] bg-gradient-to-br from-primary-300 to-primary-700"
				/>
			</div>
		</div>
	);
}
